import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User, UserCircle } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, canAccessPage } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const allNavLinks = [
    { path: '/', label: 'Dashboard', page: 'dashboard' },
    { path: '/accounts', label: 'Accounts', page: 'accounts' },
    { path: '/groups', label: 'Groups', page: 'groups' },
    { path: '/comparison', label: 'Comparison', page: 'groups' },
    { path: '/members', label: 'Members', page: 'members' },
    { path: '/analytics', label: 'Analytics', page: 'analytics' },
    { path: '/custom-groups', label: 'Custom Groups', page: 'admin' },
    { path: '/admin', label: 'Admin', page: 'admin' },
  ];

  // Filter nav links based on user permissions
  const navLinks = allNavLinks.filter(link => {
    if (!user) return false;
    return canAccessPage(link.page);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <nav className="bg-[#0e4842] shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            <div className="flex md:space-x-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                    location.pathname === link.path
                      ? 'bg-[#32ff7e] text-[#0e4842] font-semibold'
                      : 'text-white hover:bg-[#14a085] hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* User Menu */}
            {user && (
              <div className="relative ml-4" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md bg-[#14a085] hover:bg-[#32ff7e] text-white font-medium text-sm transition-colors focus:outline-none"
                >
                  <User className="h-4 w-4" />
                  <span>{user.username}</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-[#e2e8f0] overflow-hidden z-50 dropdown-menu">
                    {/* User Info Header */}
                    <div className="px-5 py-4 bg-gradient-to-r from-[#0e4842] to-[#14a085]">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-[#32ff7e] flex items-center justify-center">
                          <User className="h-5 w-5 text-[#0e4842]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{user.username}</p>
                          <p className="text-xs text-[#c6f6d5] truncate mt-0.5">{user.email}</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-[#14a085]/30">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#32ff7e] text-[#0e4842]">
                          {user.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="py-2">
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full text-left px-5 py-3 text-sm text-[#2d3748] hover:bg-[#f0fdf4] hover:text-[#0e4842] flex items-center space-x-3 transition-all duration-150 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#f0fdf4] group-hover:bg-[#32ff7e] flex items-center justify-center transition-colors">
                          <UserCircle className="h-4 w-4 text-[#14a085] group-hover:text-[#0e4842] transition-colors" />
                        </div>
                        <span className="font-medium">My Profile</span>
                      </Link>
                      <div className="mx-4 my-1 h-px bg-[#e2e8f0]"></div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-5 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-all duration-150 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                          <LogOut className="h-4 w-4 text-red-600" />
                        </div>
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:text-[#32ff7e] focus:outline-none"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-base font-medium transition-colors no-underline ${
                    location.pathname === link.path
                      ? 'bg-[#32ff7e] text-[#0e4842] font-semibold'
                      : 'text-white hover:bg-[#14a085] hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <div className="px-3 py-2 border-t border-[#14a085] mt-2 pt-2">
                  <p className="text-white text-sm mb-1">{user.username}</p>
                  <p className="text-white text-xs opacity-75">{user.email}</p>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mt-2 flex items-center space-x-2 text-white hover:text-[#32ff7e]"
                  >
                    <UserCircle className="h-4 w-4" />
                    <span>My Profile</span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="mt-2 flex items-center space-x-2 text-red-300 hover:text-red-200"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

